import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Text,
  Spinner,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import showToast from "hooks/useCustomToast";
import DocModal from "../../components/eva/DocModal";
import EvaLeftPanel from "../../components/eva/EvaLeftPanel";
import EvaRightPanel from "../../components/eva/EvaRightPanel";
import { AgentConfig, ToolConfig, PromptConfig } from "build-ts/agent_config";
import { createNewAgent, getToolsList, getSystemPromptDocumentation } from "services/EvaServices";

const EvaAgentCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [promptEditor, setPromptEditor] = useState("");
  const [agentConfig, setAgentConfig] = useState<AgentConfig>({
    id: "",
    name: "",
    agent_type: undefined,
    description: "",
    is_active: true,
    analysis_flag: JSON.stringify({
      include_tree_insights_in_analysis: false,
    }),
    version: 1,
    prompt_config: {
      model_name: "gpt-4o",
      model_type: "openai",
      temperature: 0,
      philosophy: promptEditor.trim() || "",
      instructions: "",
      name: "",
    },
    tools: [],
  });

  // Separate prompt configuration state
  const [promptConfig, setPromptConfig] = useState<PromptConfig>({
    name: "",
    model_name: "gpt-4o",
    model_type: "openai",
    temperature: 0,
    philosophy: promptEditor.trim() || "",
    instructions: "",
    is_active: true,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [availableTools, setAvailableTools] = useState<ToolConfig[]>([]);
  const [, setIsSaving] = useState(false);

  // Documentation modal states
  const [availableVariables, setAvailableVariables] = useState<Record<string, any[]>>({});
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const { isOpen: isDocsOpen, onOpen: onDocsOpen, onClose: onDocsClose } = useDisclosure();

  const fetchToolsList = async () => {
    try {
      const params = new URLSearchParams({
        limit: "100",
        offset: "0",
        sort_by: "name",
        sort_order: "asc",
      });

      const data = await getToolsList(params);
      return data.tools || [];
    } catch (error) {
      console.error("Error fetching tools list:", error);
      showToast("Failed to fetch tools list", "", "error");
      throw error;
    }
  };

  // API call to fetch system prompt documentation
  const fetchSystemPromptDocumentation = async () => {
    try {
      setIsLoadingDoc(true);
      const data = await getSystemPromptDocumentation();
      // Set the grouped variables directly from the API response
      if (data) {
        setAvailableVariables(data);
      } else {
        setAvailableVariables({});
      }
    } catch (error) {
      console.error("Error fetching system prompt documentation:", error);
      showToast("Failed to fetch documentation", "", "error");
      setAvailableVariables({});
    } finally {
      setIsLoadingDoc(false);
    }
  };

  const handleViewDocs = async () => {
    onDocsOpen();
    // Fetch documentation when modal opens
    await fetchSystemPromptDocumentation();
  };

  // Load tools list on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const toolsData = await fetchToolsList();
        setAvailableTools(toolsData);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfigChange = (field: string, value: any) => {
    setAgentConfig(prev => {
      // Handle nested field updates (e.g., "prompt_config.model_name")
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "prompt_config") {
          return {
            ...prev,
            prompt_config: {
              ...prev.prompt_config,
              [childField]: value
            }
          };
        }
      }

      // Handle analysis_flag updates
      if (field === "analysis_flag") {
        return {
          ...prev,
          analysis_flag: typeof value === "string" ? value : JSON.stringify(value)
        };
      }

      // Handle other direct field updates
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleToolsChange = (selectedTools: ToolConfig[]) => {
    setAgentConfig(prev => ({
      ...prev,
      tools: selectedTools
    }));
  };

  const handlePromptConfigChange = (field: string, value: any) => {
    // Update separate prompt config state
    setPromptConfig(prev => ({
      ...prev,
      [field]: value
    }));

    // Also update agent config's prompt_config for consistency
    setAgentConfig(prev => ({
      ...prev,
      prompt_config: {
        ...prev.prompt_config,
        [field]: value
      }
    }));
  };

  const handlePromptEditorChange = (value: string) => {
    setPromptEditor(value);
    setPromptConfig(prev => ({
      ...prev,
      philosophy: value
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!agentConfig.name?.trim()) {
      showToast("Validation Error", "Agent name is required", "error");
      return;
    }

    if (!agentConfig.agent_type) {
      showToast("Validation Error", "Agent type is required", "error");
      return;
    }

    setIsSaving(true);
    try {
      const agentData = {
        name: agentConfig.name?.trim() || "",
        description: agentConfig.description?.trim() || "",
        agent_type: agentConfig.agent_type,
        is_active: agentConfig.is_active,
        analysis_flag: agentConfig.analysis_flag,
        version: agentConfig.version || 1,
        prompt_config: {
          name: promptConfig.name || "",
          model_name: promptConfig.model_name || "gpt-4o",
          model_type: promptConfig.model_type || "openai",
          temperature: promptConfig.temperature || 0,
          philosophy: promptEditor.trim() || "",
          instructions: "",
        },
        tools: agentConfig.tools,
      };

      const createdAgent = await createNewAgent(agentData);

      // Only proceed if agent creation was successful
      if (createdAgent) {
        showToast("Success", "Agent created successfully", "success");
        // Navigate to the created agent's detail page
        navigate(`/old-eva/${createdAgent.id}`);
      }
    } catch (error: any) {
      showToast("Failed to create agent", error?.message || "Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/old-eva");
  };


  if (isLoading) {
    return (
      <Box className="eva-container eva-small-scrollbar" display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading agent data...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box className="eva-container eva-small-scrollbar">
      <Flex className="eva-main-layout">
        {/* Left Panel - Agent Configuration */}
        <EvaLeftPanel
          agentConfig={agentConfig}
          availableTools={availableTools}
          onConfigChange={handleConfigChange}
          onToolsChange={handleToolsChange}
          isCreateMode={true}
          onCancel={handleCancel}
          onSave={handleSave}
          onViewDocs={handleViewDocs}
        />

        {/* Right Panel - Prompt Editor */}
        <EvaRightPanel
          agentConfig={agentConfig}
          promptConfig={promptConfig}
          promptEditor={promptEditor}
          onPromptEditorChange={handlePromptEditorChange}
          onPromptConfigChange={handlePromptConfigChange}
          analysis_flag={agentConfig.analysis_flag || ""}
          onConfigChange={handleConfigChange}
          isCreateMode={true}
        />
      </Flex>

      {/* Variables Documentation Modal */}
      <DocModal
        isDocsOpen={isDocsOpen}
        onDocsClose={onDocsClose}
        isLoadingDoc={isLoadingDoc}
        availableVariables={availableVariables}
      />
    </Box>
  );
};

export default EvaAgentCreatePage;
